import {useState} from "react";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, Stack, Tooltip, Typography} from "@mui/material";
import type {AnalystInsight} from "../../../../../sdo/AnalystInsight.ts";
import InsightEditor from "./InsightEditor.tsx";
import {hintColor} from "../../../../../AppStyles.ts";

interface InsightCardProps {
    insight: AnalystInsight;
    onDelete: (uid: string) => void;
    onUpdate: (updated: AnalystInsight) => void;
    startInEditMode?: boolean;
}

function formatDateTime(epochMs: number): string {
    if (!epochMs || epochMs <= 0) return "";
    const d = new Date(epochMs);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function InsightCard({insight, onDelete, onUpdate, startInEditMode = false}: InsightCardProps) {
    const [editing, setEditing] = useState(startInEditMode);
    const [draft, setDraft] = useState<AnalystInsight>(insight);
    const [confirmDelete, setConfirmDelete] = useState(false);
    // Track whether this entry has ever been saved. New entries (startInEditMode=true)
    // begin un-saved; if the user cancels before saving, discard them entirely.
    const [everSaved, setEverSaved] = useState(!startInEditMode);

    const handleEdit = () => {
        setDraft(insight);
        setEditing(true);
    };

    const handleCancel = () => {
        if (!everSaved) {
            // Brand-new entry the user abandoned — discard it instead of leaving a phantom row
            onDelete(insight.uid);
            return;
        }
        setDraft(insight);
        setEditing(false);
    };

    const handleSave = () => {
        const updated: AnalystInsight = {
            ...draft,
            modifiedAt: Date.now(),
        };
        onUpdate(updated);
        setEverSaved(true);
        setEditing(false);
    };

    const handleDeleteClick = () => {
        setConfirmDelete(true);
    };

    const handleDeleteConfirm = () => {
        setConfirmDelete(false);
        onDelete(insight.uid);
    };

    const handleDeleteCancel = () => {
        setConfirmDelete(false);
    };

    const titleText = insight.title && insight.title.trim().length > 0
        ? insight.title
        : "Untitled Insight";

    const createdLine = [insight.createdBy || "", formatDateTime(insight.createdAt)]
        .filter((s) => s.length > 0)
        .join(" | ");

    return (
        <Box sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            width: "100%",
            minWidth: "500px",
            background: "linear-gradient(155deg, rgba(0, 71, 106, 0.45) 0%, rgba(0, 38, 53, 0.45) 25%, rgba(0, 0, 0, 0.45) 90%, rgba(0, 0, 0, 0.55) 100%)",
            borderRadius: "15px",
            border: editing ? "1px solid rgba(2, 207, 238, 0.45)" : "0.5px solid #3F464F",
            overflow: "hidden",
            transition: "border-color 0.2s",
        }}>
            {/* Left action panel */}
            <Tooltip title={editing ? "Save Changes" : "Edit Insight"} arrow placement="left" slotProps={{tooltip: {sx: {background: "linear-gradient(to right, rgba(41, 53, 64, 0.775), rgba(13, 14, 15, 0.775))", border: "1.5px solid #293540", borderRadius: "10px"}}, arrow: {sx: {color: "#293540"}}}}>
                <Box
                    onClick={editing ? handleSave : handleEdit}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "10px",
                        ...(editing ? {backgroundColor: "rgba(38, 42, 48, 0.95)"} : {}),
                        opacity: 0.7,
                        cursor: "pointer",
                        transition: "opacity 0.15s",
                        "&:hover": {opacity: 1},
                    }}
                >
                    {editing ? <img src="/images/save.svg" width={22} height={22} alt="Save" style={{filter: "brightness(0) saturate(100%) invert(73%) sepia(81%) saturate(413%) hue-rotate(88deg) brightness(104%) contrast(104%)"}}/> : <img src="/images/edit.svg" width={22} height={22} alt="Edit"/>}
                </Box>
            </Tooltip>

            {/* Center: header + content */}
            <Box sx={{flex: 1, minWidth: 0, display: "flex", flexDirection: "column", background: "linear-gradient(180deg, rgba(22, 30, 38, 0.9) 0%, rgba(14, 18, 24, 0.95) 100%)", borderLeft: "1px solid rgba(63, 70, 79, 0.3)", borderRight: "1px solid rgba(63, 70, 79, 0.3)"}}>
                {/* Header row */}
                <Box sx={{borderBottom: "1px solid rgba(63, 70, 79, 0.5)", padding: "8px 12px"}}>
                    <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={1.5} sx={{
                        width: "100%",
                        maxWidth: 760,
                        mx: "auto",
                    }}>
                        <img src="/images/analyst_insight.svg" width={24} height={24} alt="Analyst Insight" style={{opacity: 0.9}}/>
                        <Typography sx={{
                            color: "#f2f5fa",
                            fontWeight: 700,
                            fontSize: "1.00rem",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "360px",
                        }}>
                            {titleText}
                        </Typography>
                        <Box sx={{
                            width: "1px",
                            height: "20px",
                            backgroundColor: "rgba(112, 147, 160, 0.4)",
                            marginLeft: "4px",
                        }}/>
                        <Typography sx={{
                            color: "#02cfee",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "320px",
                        }}>
                            {createdLine || "—"}
                        </Typography>
                    </Stack>
                </Box>

                {/* Content area */}
                {editing ? (
                    <Box sx={{padding: "10px 16px"}}>
                        <Typography sx={{textAlign: "center", color: "#38ff82", fontSize: "0.80rem", padding: "2px 0 10px"}}>
                            Edit Analyst Insight
                        </Typography>
                        <Box sx={{
                            border: "1px solid rgba(63, 70, 79, 0.4)",
                            borderRadius: "8px",
                            padding: "12px",
                            backgroundColor: "rgba(0, 0, 0, 0.25)",
                        }}>
                            <InsightEditor draft={draft} editing={editing} onChange={setDraft}/>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{padding: "10px 14px", minHeight: "40px"}}>
                        <InsightEditor draft={insight} editing={editing} onChange={setDraft}/>
                    </Box>
                )}
            </Box>

            {/* Right action panel */}
            <Tooltip title={editing ? "Close Without Saving" : "Delete Insight"} arrow placement="right" slotProps={{tooltip: {sx: {background: "linear-gradient(to right, rgba(41, 53, 64, 0.775), rgba(13, 14, 15, 0.775))", border: "1.5px solid #293540", borderRadius: "10px"}}, arrow: {sx: {color: "#293540"}}}}>
                <Box
                    onClick={editing ? handleCancel : handleDeleteClick}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "10px",
                        ...(editing ? {backgroundColor: "rgba(38, 42, 48, 0.95)"} : {}),
                        opacity: 0.7,
                        cursor: "pointer",
                        transition: "opacity 0.15s",
                        "&:hover": {opacity: 1},
                    }}
                >
                    {editing ? <img src="/images/close.svg" width={22} height={22} alt="Cancel"/> : <img src="/images/delete.svg" width={22} height={22} alt="Delete"/>}
                </Box>
            </Tooltip>

            {/* Delete confirmation dialog */}
            <Dialog
                open={confirmDelete}
                onClose={handleDeleteCancel}
                slotProps={{paper: {sx: {backgroundColor: "#1a2730", border: "1px solid #3F464F", borderRadius: "12px"}}}}
            >
                <DialogContent>
                    <DialogContentText sx={{color: "#f2f5fa", fontSize: "0.90rem"}}>
                        Are you sure you want to delete the insight entry?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{padding: "8px 16px 12px"}}>
                    <Button onClick={handleDeleteCancel} sx={{color: "#7093a0", fontSize: "0.80rem"}}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} sx={{color: hintColor, fontSize: "0.80rem"}}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default InsightCard;